import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BadgeEuro, FileCode2, FileText, Pencil, Plus, ShieldAlert, Undo2 } from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import type { CustomersGateway } from '@forq/customers-data';
import type { OrderGateway } from '@forq/orders-data';
import { emptyCompanyProfile, type CompanyProfileGateway, type TenantCompanyProfile } from '@forq/platform-data';
import type { MastersGateway, MasterEntry } from '@forq/masters-data';
import type { MachinesGateway } from '@forq/machines-data';
import type { MasterdataGateway } from '@forq/masterdata-data';
import { buyerFromCustomer, buyerIssues, en16931IssueMessages, validateEn16931 } from '@forq/finance-data';
import { sellerSettingsIssues } from '@forq/finance-data';
// EINE Quelle für die Verkäuferangaben: die Grundkonfiguration des Mandanten, nicht der Browser.
import { sellerSettingsFromProfile } from './companySettings';
import { archiveIssuedInvoice, renderInvoiceForArchive } from './archiveInvoice';
import type { ArchivedDocument, DocumentArchiveGateway } from '@forq/contracts';
import { buildXRechnung, downloadXRechnung, xRechnungFileName } from '@forq/finance-data';
import { FinanceNav } from './FinanceNav';
import { buildInvoiceFlow } from './invoiceFlow';
import { CustomerSearchField } from '../shared/CustomerSearchField';
import {
  DataTable,
  type DataTableColumn,
  FlowTrail,
  CustomizableMetrics,
  MetricTile,
  RecordField,
  RecordPanel,
  SearchSelect,
  StatusPill,
  type StatusTone,
  Tabs,
  type TabItem,
  WorkspacePage,
  confirmDiscard,
} from '@forq/ui';
import {
  invoiceFlowTransitions,
  invoiceSourceLabels as sourceLabels,
  invoiceStatusLabels,
  isInvoiceOverdue,
  lineKindLabels,
  paymentStatusLabels,
  todayIso,
  type FinanceGateway,
  type Invoice,
  type InvoiceDocumentStatus,
  allowedCashDiscount,
  allTaxCodes,
  cashDiscountDeadline,
  openAmount as offenerBetrag,
  type RecordPaymentInput,
  calculateInvoiceLineNet,
  taxCodeDefinition,
  taxCodeForRate,
  type TaxCode,
  type InvoiceLineInput,
  type InvoiceLineKind,
  type InvoicePaymentStatus,
  type InvoiceSource,
} from '@forq/finance-data';

interface FinancePageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly financeGateway: FinanceGateway;
  readonly ordersGateway: OrderGateway;
  readonly customersGateway: CustomersGateway;
  readonly companyGateway: CompanyProfileGateway;
  readonly mastersGateway: MastersGateway;
  /** Für die Warengruppe je Rechnungszeile (Stufe 3): löst Maschine/Artikel der Vorgangsposition auf. */
  readonly machinesGateway: MachinesGateway;
  readonly masterdataGateway: MasterdataGateway;
  /** Belegarchiv (§ 14b UStG): der festgeschriebene Beleg wird abgelegt und von dort gezeigt. */
  readonly archiveGateway: DocumentArchiveGateway;
}

/** Sinnvolle Vorbelegung der Beleg-Kostenstelle nach Herkunft — der Nutzer kann sie im Entwurf ändern. */
const costCenterCodeForSource: Partial<Record<InvoiceSource, string>> = {
  order: 'HANDEL', sales: 'HANDEL', service: 'WERKSTATT', rental: 'MIETE',
};
function suggestCostCenterId(costCenters: readonly MasterEntry[], source: InvoiceSource): string | null {
  const code = costCenterCodeForSource[source];
  if (!code) return null;
  return costCenters.find((entry) => entry.code.toLowerCase() === code.toLowerCase())?.id ?? null;
}

type FinanceView = 'all' | 'work' | 'open' | 'paid';

const documentTone: Record<InvoiceDocumentStatus, StatusTone> = {
  draft: 'neutral', in_review: 'warning', approved: 'info', invoiced: 'info', sent: 'progress', cancelled: 'danger',
};
const paymentTone: Record<InvoicePaymentStatus, StatusTone> = {
  not_relevant: 'neutral', open: 'warning', partial: 'warning', paid: 'done',
};

function money(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}
function dateLabel(value: string | null): string {
  return value ? new Intl.DateTimeFormat('de-DE').format(new Date(`${value}T00:00:00`)) : '–';
}
function errorMessage(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Der Vorgang konnte nicht verarbeitet werden.';
}

function matchesView(invoice: Invoice, view: FinanceView): boolean {
  if (view === 'all') return true;
  if (view === 'work') return ['draft', 'in_review', 'approved'].includes(invoice.documentStatus);
  if (view === 'paid') return invoice.paymentStatus === 'paid';
  return invoice.paymentStatus === 'open' || invoice.paymentStatus === 'partial';
}

export function FinancePage({ tenant, mode, permissions, financeGateway, ordersGateway, customersGateway, companyGateway, mastersGateway, machinesGateway, masterdataGateway, archiveGateway }: FinancePageProps) {
  const navigate = useNavigate();
  const { nummer } = useParams();
  const [invoices, setInvoices] = useState<readonly Invoice[]>([]);
  const [view, setView] = useState<FinanceView>('all');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [eInvoiceIssues, setEInvoiceIssues] = useState<readonly string[]>([]);
  const [editing, setEditing] = useState<Invoice | null>(null);
  /** Belegnummern, deren Archivierung fehlgeschlagen ist -- sichtbar, bis sie nachgeholt wurde. */
  const [archiveGap, setArchiveGap] = useState<readonly string[]>([]);
  /** Die Rechnung, für die gerade eine Zahlung erfasst wird. */
  const [payingInvoice, setPayingInvoice] = useState<Invoice | null>(null);
  const [company, setCompany] = useState<TenantCompanyProfile>(emptyCompanyProfile);
  const [pdfInvoice, setPdfInvoice] = useState<Invoice | null>(null);
  const [costCenters, setCostCenters] = useState<readonly MasterEntry[]>([]);
  const [warengruppen, setWarengruppen] = useState<readonly MasterEntry[]>([]);

  useEffect(() => { void companyGateway.loadProfile(tenant.id).then(setCompany).catch(() => setCompany(emptyCompanyProfile)); }, [companyGateway, tenant.id]);
  useEffect(() => {
    let active = true;
    void Promise.all([mastersGateway.listCostCenters(tenant.id), mastersGateway.listWarengruppen(tenant.id)])
      .then(([cc, wg]) => { if (active) { setCostCenters(cc); setWarengruppen(wg); } })
      .catch(() => { if (active) { setCostCenters([]); setWarengruppen([]); } });
    return () => { active = false; };
  }, [mastersGateway, tenant.id]);

  const canCreate = permissions.has('finance.invoices.edit');
  const canRecordPayment = permissions.has('finance.payments.record');
  const today = todayIso();

  /**
   * Produces the XRechnung, or explains exactly what is missing. An e-invoice is a legally
   * mandated document: emitting an incomplete one is worse than emitting none, so every gap is
   * reported by the record it belongs to (our own master data vs. the customer's).
   */
  const exportEInvoice = useCallback(async (invoice: Invoice) => {
    setBusy(true);
    setError(null);
    setEInvoiceIssues([]);
    try {
      const seller = sellerSettingsFromProfile(company);
      const issues = [...sellerSettingsIssues(seller)];

      const page = await customersGateway.searchCustomers({ tenantId: tenant.id, query: invoice.customerNumber, pageSize: 5 });
      const match = page.items.find((item) => item.number === invoice.customerNumber);
      if (!match) {
        issues.push(`Kunde ${invoice.customerNumber} ist im Kundenstamm nicht auffindbar.`);
        setEInvoiceIssues(issues);
        return;
      }

      const customer = await customersGateway.getCustomer(tenant.id, match.id);
      const buyer = buyerFromCustomer(customer, invoice);
      issues.push(...buyerIssues(buyer, invoice, customer.agreements.leitwegId));
      if (issues.length > 0) {
        setEInvoiceIssues(issues);
        return;
      }

      // Letzte Instanz vor dem Erzeugen: die Geschäftsregeln der EN 16931. Eine vom Empfänger
      // zurückgewiesene Rechnung gilt als nicht gestellt -- die Frist läuft trotzdem weiter.
      // Deshalb lieber gar keine Datei als eine, die abgewiesen wird.
      const verstoesse = en16931IssueMessages(validateEn16931(invoice, seller, buyer));
      if (verstoesse.length > 0) {
        setEInvoiceIssues(verstoesse);
        return;
      }

      const xml = buildXRechnung(invoice, seller, buyer);
      if (!xml) {
        setEInvoiceIssues(['Die Rechnung enthält einen Steuersatz, der sich nicht abbilden lässt.']);
        return;
      }
      downloadXRechnung(xml, xRechnungFileName(invoice));
    } catch (exportError) {
      setError(errorMessage(exportError));
    } finally {
      setBusy(false);
    }
  }, [tenant.id, customersGateway, company]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const invoiceList = await financeGateway.listInvoices({ tenantId: tenant.id });
      setInvoices(invoiceList);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [financeGateway, tenant.id]);

  useEffect(() => { void load(); }, [load]);

  const selected = nummer
    ? invoices.find((invoice) => invoice.invoiceNumber === nummer || invoice.proposalNumber === nummer) ?? null
    : null;

  const metrics = useMemo(() => {
    const openItems = invoices.filter((i) => i.paymentStatus === 'open' || i.paymentStatus === 'partial');
    return {
      openCount: openItems.length,
      openSum: openItems.reduce((sum, i) => sum + (i.grossTotal - i.paidAmount), 0),
      overdue: invoices.filter((i) => isInvoiceOverdue(i, today)).length,
      drafts: invoices.filter((i) => ['draft', 'in_review', 'approved'].includes(i.documentStatus)).length,
      paidSum: invoices.filter((i) => i.paymentStatus === 'paid').reduce((sum, i) => sum + i.grossTotal, 0),
    };
  }, [invoices, today]);

  /**
   * Den festgeschriebenen Beleg archivieren (§ 14b UStG). Getrennt aufrufbar, damit ein
   * fehlgeschlagener Versuch WIEDERHOLT werden kann -- die Rechnung selbst ist dann schon
   * gestellt, und eine gestellte Rechnung ohne Archiveintrag darf nicht stillschweigend
   * liegenbleiben.
   */
  const archiveInvoice = useCallback(async (invoice: Invoice): Promise<boolean> => {
    if (!invoice.invoiceNumber) return true;
    try {
      await archiveIssuedInvoice(tenant.id, invoice, company, customersGateway, archiveGateway);
      setArchiveGap((current) => current.filter((number) => number !== invoice.invoiceNumber));
      return true;
    } catch (archiveError) {
      setArchiveGap((current) => current.includes(invoice.invoiceNumber!) ? current : [...current, invoice.invoiceNumber!]);
      setError(`Der Beleg ${invoice.invoiceNumber} wurde erstellt, konnte aber NICHT archiviert werden: ${errorMessage(archiveError)} Bitte über „Archivierung nachholen" erneut versuchen -- die Aufbewahrung ist gesetzlich vorgeschrieben.`);
      return false;
    }
  }, [archiveGateway, company, customersGateway, tenant.id]);

  const advance = useCallback(async (invoice: Invoice, toStatus: InvoiceDocumentStatus) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await financeGateway.transitionInvoice({ tenantId: tenant.id, invoiceId: invoice.id, toStatus });
      setInvoices((current) => current.map((item) => item.id === updated.id ? updated : item));
      // Erst jetzt trägt der Beleg seine gesetzliche Nummer -- deshalb wird hier archiviert.
      if (updated.invoiceNumber && !invoice.invoiceNumber) {
        const archived = await archiveInvoice(updated);
        if (archived) navigate(`/finance/rechnungen/${updated.invoiceNumber}`);
        return;
      }
    } catch (transitionError) {
      setError(errorMessage(transitionError));
    } finally {
      setBusy(false);
    }
  }, [archiveInvoice, financeGateway, navigate, tenant.id]);

  const pay = useCallback(async (invoice: Invoice, input: Omit<RecordPaymentInput, 'tenantId' | 'invoiceId'>) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await financeGateway.recordPayment({ tenantId: tenant.id, invoiceId: invoice.id, ...input });
      setInvoices((current) => current.map((item) => item.id === updated.id ? updated : item));
      setPayingInvoice(null);
    } catch (paymentError) {
      setError(errorMessage(paymentError));
    } finally {
      setBusy(false);
    }
  }, [financeGateway, tenant.id]);

  const issueCreditNote = useCallback(async (invoice: Invoice) => {
    if (!window.confirm(`Gutschrift zu ${invoice.invoiceNumber} erstellen? Die Forderung wird storniert.`)) return;
    setBusy(true);
    setError(null);
    try {
      const credit = await financeGateway.createCreditNote({ tenantId: tenant.id, invoiceId: invoice.id });
      // Die Gutschrift traegt eine eigene gesetzliche Nummer -- also auch ein eigener Archiveintrag.
      await archiveInvoice(credit);
      // GoBD-Reopen: die stornierte Rechnung entstammt einem Vorgang -> den Vorgang wieder zur
      // Abrechnung öffnen, damit er korrigiert und neu abgerechnet werden kann. Best-effort: ein
      // fehlender Vorgang oder fehlende Rechte lassen die Gutschrift trotzdem stehen.
      if (invoice.sourceType === 'order' && invoice.sourceNumber) {
        try {
          const page = await ordersGateway.searchOrders({ tenantId: tenant.id, query: invoice.sourceNumber, bucket: 'all', pageSize: 10 });
          const order = page.items.find((item) => item.number === invoice.sourceNumber);
          if (order && (order.status === 'billed' || order.status === 'completed')) {
            await ordersGateway.transitionOrder({ tenantId: tenant.id, orderId: order.id, toStatus: 'billing-ready', reason: `Gutschrift ${credit.invoiceNumber ?? ''} – Vorgang wieder geöffnet` });
          }
        } catch { /* Reopen ist nachrangig – die Gutschrift steht bereits. */ }
      }
      await load();
      navigate(`/finance/rechnungen/${credit.invoiceNumber}`);
    } catch (creditError) {
      setError(errorMessage(creditError));
    } finally {
      setBusy(false);
    }
  }, [financeGateway, ordersGateway, load, navigate, tenant.id]);

  const submitEdit = useCallback(async (fields: { customerNumber: string; customerName: string; paymentTermDays: number; costCenterId: string | null; serviceDate: string | null; serviceEndDate: string | null; lines: readonly InvoiceLineInput[] }) => {
    if (!editing) return;
    setBusy(true); setError(null);
    try {
      await financeGateway.updateInvoice({ tenantId: tenant.id, invoiceId: editing.id, ...fields });
      setEditing(null);
      await load();
    } catch (editError) {
      setError(errorMessage(editError));
    } finally {
      setBusy(false);
    }
  }, [editing, financeGateway, load, tenant.id]);

  const columns: readonly DataTableColumn<Invoice>[] = [
    {
      key: 'number', header: 'Rechnung', width: 'minmax(0, 1.2fr)',
      render: (invoice) => (
        <span className="forq-stack">
          <strong>{invoice.invoiceNumber ?? invoice.proposalNumber}</strong>
          <small>{invoice.kind === 'credit_note' ? `Gutschrift zu ${invoice.creditedInvoiceNumber}` : invoice.invoiceNumber ? invoice.proposalNumber : 'noch keine Rechnungsnummer'}</small>
        </span>
      ),
    },
    {
      key: 'customer', header: 'Kunde / Quelle', width: 'minmax(0, 1.4fr)',
      render: (invoice) => (
        <span className="forq-stack"><strong>{invoice.customerName}</strong><small>{sourceLabels[invoice.sourceType]} {invoice.sourceNumber}</small></span>
      ),
    },
    { key: 'gross', header: 'Brutto', width: '120px', align: 'end', render: (invoice) => <span className="forq-cell-num">{money(invoice.grossTotal)}</span> },
    { key: 'document', header: 'Beleg', render: (invoice) => <StatusPill label={invoiceStatusLabels[invoice.documentStatus]} tone={documentTone[invoice.documentStatus]} /> },
    {
      key: 'payment', header: 'Zahlung',
      render: (invoice) => <StatusPill label={isInvoiceOverdue(invoice, today) ? 'Überfällig' : paymentStatusLabels[invoice.paymentStatus]} tone={isInvoiceOverdue(invoice, today) ? 'danger' : paymentTone[invoice.paymentStatus]} />,
    },
    { key: 'due', header: 'Fällig', width: '110px', render: (invoice) => dateLabel(invoice.dueDate) },
  ];

  const viewTabs: readonly TabItem[] = [
    { id: 'all', label: 'Alle' },
    { id: 'work', label: 'In Arbeit', badge: metrics.drafts || undefined },
    { id: 'open', label: 'Offene Posten', badge: metrics.openCount || undefined },
    { id: 'paid', label: 'Bezahlt' },
  ];

  const rows = invoices.filter((invoice) => matchesView(invoice, view));
  const transitions = selected ? invoiceFlowTransitions(selected, permissions) : [];
  // Offen = brutto minus AUSGEGLICHEN (Geld + Skonto + Ausbuchung). Mit paidAmount gerechnet
  // bliebe eine mit Skonto beglichene Rechnung ewig offen.
  const openAmount = selected ? offenerBetrag(selected) : 0;
  const canCredit = permissions.has('finance.invoices.cancel');
  const creditable = !!selected && selected.kind === 'invoice' && !!selected.invoiceNumber
    && !selected.creditNoteNumber && (selected.documentStatus === 'invoiced' || selected.documentStatus === 'sent');

  return (
    <WorkspacePage
      customizeKey="finance-abrechnung"
      eyebrow={`FORQ Finance · ${mode === 'demo' ? 'Demo' : 'Supabase'}`}
      title="Ausgangsrechnungen"
      description="Register der ausgestellten Rechnungen. Abgerechnet wird jetzt im Vorgang – hier laufen die Belege ein und sind nach dem Versand schreibgeschützt."
      metrics={(
        <CustomizableMetrics
          pageKey="finance-abrechnung"
          tiles={[
            { key: 'open', node: <MetricTile label="Offene Posten" value={money(metrics.openSum)} hint={`${metrics.openCount} Rechnungen`} tone="info" /> },
            { key: 'overdue', node: <MetricTile label="Überfällig" value={metrics.overdue} hint="über Fälligkeit" tone={metrics.overdue > 0 ? 'danger' : 'default'} /> },
            { key: 'drafts', node: <MetricTile label="In Arbeit" value={metrics.drafts} hint="Entwurf bis Freigabe" /> },
            { key: 'paid', node: <MetricTile label="Bezahlt" value={money(metrics.paidSum)} hint="Umsatz erfasst" tone="success" /> },
          ]}
        />
      )}
      toolbar={<div className="finance-toolbar"><FinanceNav /><Tabs items={viewTabs} activeId={view} onSelect={(id) => setView(id as FinanceView)} ariaLabel="Rechnungsfilter" /></div>}
    >
      {archiveGap.length > 0 && (
        <div className="finance-archive-gap" role="alert">
          <ShieldAlert size={16} aria-hidden />
          <div>
            <strong>{archiveGap.length === 1 ? 'Ein Beleg ist nicht archiviert' : `${archiveGap.length} Belege sind nicht archiviert`}</strong>
            <span>
              {archiveGap.join(', ')} — die Aufbewahrung des ausgestellten Belegs ist nach § 14b UStG
              vorgeschrieben. Bitte nachholen; die Rechnung selbst ist bereits gestellt.
            </span>
          </div>
          <button
            type="button"
            className="finance-actions__btn"
            disabled={busy}
            onClick={() => void (async () => {
              setBusy(true);
              for (const number of archiveGap) {
                const invoice = invoices.find((item) => item.invoiceNumber === number);
                if (invoice) await archiveInvoice(invoice);
              }
              setBusy(false);
            })()}
          >
            Archivierung nachholen
          </button>
        </div>
      )}
      {error && !editing && <div className="finance-page__error">{error}</div>}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(invoice) => invoice.id}
        onRowSelect={(invoice) => navigate(`/finance/rechnungen/${invoice.invoiceNumber ?? invoice.proposalNumber}`)}
        isRowSelected={(invoice) => selected?.id === invoice.id}
        loading={loading}
        loadingLabel="Rechnungen werden geladen …"
        emptyLabel="Keine Rechnungen in dieser Ansicht."
        ariaLabel="Rechnungsliste"
      />

      {selected && (
        <RecordPanel
          eyebrow="Rechnung"
          title={`${selected.invoiceNumber ?? selected.proposalNumber} · ${selected.customerName}`}
          subtitle={`${sourceLabels[selected.sourceType]} ${selected.sourceNumber}`}
          status={<span className="finance-pills"><StatusPill label={invoiceStatusLabels[selected.documentStatus]} tone={documentTone[selected.documentStatus]} /><StatusPill label={isInvoiceOverdue(selected, today) ? 'Überfällig' : paymentStatusLabels[selected.paymentStatus]} tone={isInvoiceOverdue(selected, today) ? 'danger' : paymentTone[selected.paymentStatus]} /></span>}
        >
          <FlowTrail steps={buildInvoiceFlow(selected)} ariaLabel={`Belegstatus ${selected.proposalNumber}`} />

          {(transitions.length > 0 || (canRecordPayment && openAmount > 0) || (creditable && canCredit) || !!selected.invoiceNumber) && (
            <div className="finance-actions">
              {transitions.map((transition) => (
                <button key={transition.toStatus} type="button" className={`finance-actions__btn${transition.tone === 'danger' ? ' is-danger' : ''}`} disabled={busy} onClick={() => void advance(selected, transition.toStatus)}>
                  {transition.label}
                </button>
              ))}
              {canCreate && selected.documentStatus === 'draft' && (
                <button type="button" className="finance-actions__btn" disabled={busy} onClick={() => { setEditing(selected); setError(null); }}>
                  <Pencil size={14} /> Bearbeiten
                </button>
              )}
              {/* Frueher wurde hier blind der volle offene Betrag gebucht -- eine Teilzahlung
                  liess sich gar nicht erfassen, obwohl der Kern sie laengst konnte. */}
              {canRecordPayment && openAmount > 0 && (
                <button type="button" className="finance-actions__btn is-pay" disabled={busy} onClick={() => setPayingInvoice(selected)}>
                  <BadgeEuro size={14} /> Zahlung erfassen ({money(openAmount)})
                </button>
              )}
              {selected.invoiceNumber && (
                <button type="button" className="finance-actions__btn" disabled={busy} onClick={() => setPdfInvoice(selected)}>
                  <FileText size={14} /> Rechnung als PDF
                </button>
              )}
              {selected.invoiceNumber && (
                <button type="button" className="finance-actions__btn" disabled={busy} onClick={() => void exportEInvoice(selected)}>
                  <FileCode2 size={14} /> XRechnung
                </button>
              )}
              {creditable && canCredit && (
                <button type="button" className="finance-actions__btn is-danger" disabled={busy} onClick={() => void issueCreditNote(selected)}>
                  <Undo2 size={14} /> Gutschrift / Storno
                </button>
              )}
            </div>
          )}

          {eInvoiceIssues.length > 0 && (
            <div className="finance-einvoice-issues">
              <strong>E-Rechnung noch nicht möglich – es fehlt:</strong>
              <ul>{eInvoiceIssues.map((issue) => <li key={issue}>{issue}</li>)}</ul>
            </div>
          )}
          {(selected.creditedInvoiceNumber || selected.creditNoteNumber) && (
            <div className="finance-credit-ref">
              {selected.kind === 'credit_note'
                ? <>Gutschrift zu Rechnung <strong>{selected.creditedInvoiceNumber}</strong></>
                : <>Storniert durch Gutschrift <strong>{selected.creditNoteNumber}</strong></>}
            </div>
          )}

          <div className="forq-record-fields">
            <RecordField label="Netto" value={money(selected.netTotal)} />
            <RecordField label="USt." value={money(selected.vatTotal)} />
            <RecordField label="Brutto" value={money(selected.grossTotal)} />
            <RecordField label="Bezahlt" value={money(selected.paidAmount)} />
            <RecordField label="Offen" value={money(openAmount)} />
            <RecordField label="Fällig" value={dateLabel(selected.dueDate)} />
            <RecordField label="Kostenstelle" value={selected.costCenterName ?? costCenters.find((entry) => entry.id === selected.costCenterId)?.name ?? '–'} />
          </div>

          <div className="finance-lines">
            {selected.lines.map((line, index) => (
              <div className="finance-lines__row" key={index}>
                <span className="finance-lines__kind">{lineKindLabels[line.kind]}</span>
                <span className="finance-lines__desc">{line.description}</span>
                <span className="finance-lines__qty">{line.quantity} {line.unit}</span>
                <span className="finance-lines__amount">{money(line.netAmount)}</span>
              </div>
            ))}
          </div>
        </RecordPanel>
      )}

      {editing && (
        <InvoiceEditModal
          invoice={editing}
          tenantId={tenant.id}
          customersGateway={customersGateway}
          costCenters={costCenters}
          warengruppen={warengruppen}
          busy={busy}
          error={error}
          onClose={() => { setEditing(null); setError(null); }}
          onSave={submitEdit}
        />
      )}

      {payingInvoice && (
        <PaymentModal
          invoice={payingInvoice}
          company={company}
          busy={busy}
          onClose={() => setPayingInvoice(null)}
          onSave={(input) => void pay(payingInvoice, input)}
        />
      )}
      {pdfInvoice && (
        <InvoiceDocumentModal
          invoice={pdfInvoice}
          company={company}
          tenantId={tenant.id}
          customersGateway={customersGateway}
          archiveGateway={archiveGateway}
          onClose={() => setPdfInvoice(null)}
        />
      )}
    </WorkspacePage>
  );
}

interface EditableLine extends InvoiceLineInput {
  readonly _key: number;
}

/** Positions-Editor für einen Rechnungsentwurf: Kunde, Zahlungsziel und Positionen ändern. Die Summen
 *  hier sind nur die Live-Vorschau -- die verbindlichen Summen rechnet die DB beim Speichern neu. */
function InvoiceEditModal({ invoice, tenantId, customersGateway, costCenters, warengruppen, busy, error, onClose, onSave }: {
  readonly invoice: Invoice;
  readonly tenantId: string;
  readonly customersGateway: CustomersGateway;
  readonly costCenters: readonly MasterEntry[];
  readonly warengruppen: readonly MasterEntry[];
  readonly busy: boolean;
  readonly error: string | null;
  readonly onClose: () => void;
  readonly onSave: (fields: { customerNumber: string; customerName: string; paymentTermDays: number; costCenterId: string | null; serviceDate: string | null; serviceEndDate: string | null; lines: readonly InvoiceLineInput[] }) => void;
}) {
  const [customer, setCustomer] = useState<{ number: string; name: string }>({ number: invoice.customerNumber, name: invoice.customerName });
  const [paymentTermDays, setPaymentTermDays] = useState(invoice.paymentTermDays);
  // Leistungsdatum ist Pflichtangabe (§ 14 UStG) und im Entwurf aenderbar; leer = Ausstellungsdatum.
  const [serviceDate, setServiceDate] = useState(invoice.serviceDate ?? '');
  const [serviceEndDate, setServiceEndDate] = useState(invoice.serviceEndDate ?? '');
  // Noch keine Kostenstelle gesetzt? Nach Herkunft vorbelegen — der Nutzer bestätigt sie mit dem Speichern.
  const [costCenterId, setCostCenterId] = useState<string | null>(invoice.costCenterId ?? suggestCostCenterId(costCenters, invoice.sourceType));
  const keyRef = useRef(invoice.lines.length);
  const [lines, setLines] = useState<EditableLine[]>(() => invoice.lines.map((line, index) => ({
    kind: line.kind, description: line.description, quantity: line.quantity, unit: line.unit, unitPrice: line.unitPrice, discountPercent: line.discountPercent ?? 0, vatRate: line.vatRate,
    warengruppeId: line.warengruppeId ?? null, warengruppeName: line.warengruppeName ?? '', _key: index,
  })));

  const setLine = (key: number, patch: Partial<EditableLine>) => setLines((current) => current.map((line) => line._key === key ? { ...line, ...patch } : line));
  const addLine = () => setLines((current) => [...current, { kind: 'service', description: '', quantity: 1, unit: 'Stück', unitPrice: 0, discountPercent: 0, vatRate: 19, warengruppeId: null, warengruppeName: '', _key: keyRef.current++ }]);
  const removeLine = (key: number) => setLines((current) => current.filter((line) => line._key !== key));

  const totals = useMemo(() => {
    const net = Math.round(lines.reduce((sum, line) => sum + calculateInvoiceLineNet(line), 0) * 100) / 100;
    const vat = Math.round(lines.reduce((sum, line) => sum + calculateInvoiceLineNet(line) * ((line.vatRate ?? 0) / 100), 0) * 100) / 100;
    return { net, vat, gross: Math.round((net + vat) * 100) / 100 };
  }, [lines]);

  const save = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave({
      customerNumber: customer.number,
      customerName: customer.name,
      paymentTermDays,
      costCenterId,
      serviceDate: serviceDate.trim() || null,
      serviceEndDate: serviceEndDate.trim() || null,
      lines: lines.map(({ _key, ...line }) => ({ ...line, description: line.description.trim() })),
    });
  };

  return (
    <div className="finance-modal" role="dialog" aria-modal="true" aria-label={`Rechnung ${invoice.proposalNumber} bearbeiten`} onMouseDown={(e) => { if (e.target === e.currentTarget && confirmDiscard()) onClose(); }}>
      <form className="finance-modal__panel finance-modal__panel--wide" onSubmit={save}>
        <header><Pencil size={16} /> <h2>Rechnungsentwurf {invoice.proposalNumber} bearbeiten</h2></header>
        <div className="finance-modal__grid">
          <label className="finance-modal__wide">Kunde
            <CustomerSearchField tenantId={tenantId} gateway={customersGateway} initialLabel={`${invoice.customerNumber} · ${invoice.customerName}`} onSelect={(picked) => { if (picked) setCustomer(picked); }} />
          </label>
          <label>Zahlungsziel (Tage)<input type="number" min={0} value={paymentTermDays} onChange={(event) => setPaymentTermDays(Number(event.target.value) || 0)} /></label>
          <label>Leistungsdatum<input type="date" value={serviceDate} onChange={(event) => setServiceDate(event.target.value)} /></label>
          <label>Leistungszeitraum bis <small>(optional)</small><input type="date" value={serviceEndDate} onChange={(event) => setServiceEndDate(event.target.value)} /></label>
          <label>Kostenstelle
            <SearchSelect
              ariaLabel="Kostenstelle"
              value={costCenterId ?? ''}
              onChange={(value) => setCostCenterId(value || null)}
              options={[{ value: '', label: '— keine —' }, ...costCenters.map((entry) => ({ value: entry.id, label: entry.name }))]}
            />
          </label>
        </div>
        <div className="finance-editlines">
          <div className="finance-editlines__head"><span>Art</span><span>Beschreibung</span><span>Menge</span><span>Einheit</span><span>Einzelpreis</span><span>Rabatt %</span><span>Steuer</span><span>Warengruppe</span><span aria-hidden /></div>
          {lines.map((line) => (
            <div className="finance-editlines__row" key={line._key}>
              <SearchSelect ariaLabel="Art" value={line.kind} onChange={(value) => setLine(line._key, { kind: value as InvoiceLineKind })} options={(Object.keys(lineKindLabels) as InvoiceLineKind[]).map((kind) => ({ value: kind, label: lineKindLabels[kind] }))} />
              <input aria-label="Beschreibung" value={line.description} onChange={(event) => setLine(line._key, { description: event.target.value })} />
              <input aria-label="Menge" type="number" step="0.001" min="0" value={line.quantity} onChange={(event) => setLine(line._key, { quantity: Number(event.target.value) || 0 })} />
              <input aria-label="Einheit" value={line.unit} onChange={(event) => setLine(line._key, { unit: event.target.value })} />
              <input aria-label="Einzelpreis" type="number" step="0.01" min="0" value={line.unitPrice} onChange={(event) => setLine(line._key, { unitPrice: Number(event.target.value) || 0 })} />
              <input aria-label="Rabatt in Prozent" type="number" step="0.01" min="0" max="100" value={line.discountPercent ?? 0} onChange={(event) => setLine(line._key, { discountPercent: Math.min(100, Math.max(0, Number(event.target.value) || 0)) })} />
              {/* Steuerschlüssel statt blossem Prozentsatz: 0 % kann steuerfrei, § 13b, Ausfuhr,
                  innergemeinschaftlich ODER differenzbesteuert heissen -- der Satz allein sagt es nicht. */}
              <SearchSelect
                ariaLabel="Steuerschlüssel"
                value={line.taxCode ?? taxCodeForRate(line.vatRate) ?? 'S19'}
                onChange={(value) => {
                  const code = value as TaxCode;
                  setLine(line._key, { taxCode: code, vatRate: taxCodeDefinition(code).rate });
                }}
                options={allTaxCodes.map((code) => ({ value: code, label: taxCodeDefinition(code).label }))}
              />
              <SearchSelect ariaLabel="Warengruppe" value={line.warengruppeId ?? ''} onChange={(value) => setLine(line._key, { warengruppeId: value || null, warengruppeName: warengruppen.find((entry) => entry.id === value)?.name ?? '' })} options={[{ value: '', label: '—' }, ...warengruppen.map((entry) => ({ value: entry.id, label: entry.name }))]} />
              <button type="button" className="finance-editlines__del" aria-label="Position entfernen" disabled={lines.length <= 1} onClick={() => removeLine(line._key)}>×</button>
            </div>
          ))}
          <button type="button" className="finance-actions__btn finance-editlines__add" onClick={addLine}><Plus size={13} /> Position</button>
        </div>
        <div className="finance-modal__total">Netto {money(totals.net)} · zzgl. USt {money(totals.vat)} · <strong>Brutto {money(totals.gross)}</strong></div>
        {error && <div className="finance-modal__error">{error}</div>}
        <div className="finance-modal__actions">
          <button type="button" className="finance-actions__btn" onClick={onClose}>Abbrechen</button>
          <button type="submit" className="finance-actions__btn is-pay" disabled={busy || lines.length === 0}>Speichern</button>
        </div>
      </form>
    </div>
  );
}

/** Druckvorschau einer Ausgangsrechnung: holt die Rechnungsanschrift aus dem Kundenstamm und
 *  rendert den Beleg als HTML im iframe. Gedruckt / als PDF gespeichert über den Button im Beleg. */
function InvoiceDocumentModal({ invoice, company, tenantId, customersGateway, archiveGateway, onClose }: {
  readonly invoice: Invoice;
  readonly company: TenantCompanyProfile;
  readonly tenantId: string;
  readonly customersGateway: CustomersGateway;
  readonly archiveGateway: DocumentArchiveGateway;
  readonly onClose: () => void;
}) {
  const [html, setHtml] = useState<string | null>(null);
  const [archived, setArchived] = useState<ArchivedDocument | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const documentNumber = invoice.invoiceNumber ?? invoice.proposalNumber;

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        // Ein ausgestellter Beleg kommt AUS DEM ARCHIV -- so, wie er ausgestellt wurde. Nur wenn
        // keiner abgelegt ist (Entwurf, oder ein Altbeleg von vor der Archivierung), wird live
        // gerendert; dann sagt die Kopfzeile das auch.
        if (invoice.invoiceNumber) {
          const entry = await archiveGateway.getArchivedDocument(tenantId, invoice.invoiceNumber);
          if (entry && active) { setArchived(entry); setHtml(entry.contentHtml); return; }
        }
        const rendered = await renderInvoiceForArchive(tenantId, invoice, company, customersGateway);
        if (active) setHtml(rendered.html);
      } catch (fetchError) {
        if (active) setLoadError(errorMessage(fetchError));
      }
    })();
    return () => { active = false; };
  }, [invoice, company, tenantId, customersGateway, archiveGateway]);

  return (
    <div className="orders-doc-modal" role="dialog" aria-modal="true" aria-label={`Rechnung ${documentNumber}`} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="orders-doc-modal__panel">
        <header>
          <span>
            Rechnung · {documentNumber}
            {archived
              ? <em className="finance-archive-badge is-archived" title={`Prüfsumme ${archived.checksum || 'nicht gebildet'}`}>archiviert am {dateLabel(archived.archivedAt.slice(0, 10))}</em>
              : invoice.invoiceNumber
                ? <em className="finance-archive-badge is-missing">nicht archiviert · Ansicht neu erzeugt</em>
                : <em className="finance-archive-badge">Entwurf</em>}
          </span>
          <button type="button" className="orders-doc-modal__close" onClick={onClose}>Schließen</button>
        </header>
        {loadError
          ? <div className="finance-modal__error">{loadError}</div>
          : html
            ? <iframe className="orders-doc-frame" title={`Rechnung ${documentNumber}`} srcDoc={html} />
            : <p className="finance-work__empty">Beleg wird aufgebaut …</p>}
      </div>
    </div>
  );
}

/**
 * Zahlung erfassen -- mit Betrag, Wertstellung, Zahlungsart und Vermerk.
 *
 * Bis 09/2026 buchte der Knopf blind den vollen offenen Betrag; eine Teilzahlung liess sich gar
 * nicht erfassen, obwohl der Kern sie konnte. Und zahlte ein Kunde rechtmaessig mit Skonto, blieb
 * ein Rest ewig offen -- deshalb bietet die Maske den Abzug an, aber NUR wenn die Frist gewahrt
 * ist und der Rest den zulaessigen Abzug nicht uebersteigt.
 */
function PaymentModal({ invoice, company, busy, onClose, onSave }: {
  readonly invoice: Invoice;
  readonly company: TenantCompanyProfile;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onSave: (input: Omit<RecordPaymentInput, 'tenantId' | 'invoiceId'>) => void;
}) {
  const heute = todayIso();
  const offen = offenerBetrag(invoice);
  const [amount, setAmount] = useState(offen);
  const [valueDate, setValueDate] = useState(heute);
  const [method, setMethod] = useState('Überweisung');
  const [note, setNote] = useState('');

  const konditionen = {
    percent: company.documentDefaults.cashDiscountPercent,
    days: company.documentDefaults.cashDiscountDays,
  };
  const skontoFrist = cashDiscountDeadline(invoice, konditionen);
  const zulaessig = allowedCashDiscount(invoice, konditionen, valueDate);
  const restNachZahlung = Math.round((offen - amount) * 100) / 100;
  // Nach dieser Zahlung bliebe ein Rest -- darf er als Skonto weg?
  const skontoMoeglich = restNachZahlung > 0 && zulaessig > 0 && restNachZahlung <= zulaessig + 0.01;

  return (
    <div className="finance-modal" role="dialog" aria-modal="true" aria-label="Zahlung erfassen"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <form className="finance-modal__panel" onSubmit={(event) => { event.preventDefault(); onSave({ amount, valueDate, method, note }); }}>
        <header><BadgeEuro size={16} /> <h2>Zahlung zu {invoice.invoiceNumber ?? invoice.proposalNumber}</h2></header>
        <dl className="finance-settings-readout">
          <div><dt>Rechnungsbetrag</dt><dd>{money(invoice.grossTotal)}</dd></div>
          <div><dt>Bereits ausgeglichen</dt><dd>{money(invoice.settledAmount)}</dd></div>
          <div><dt>Offen</dt><dd>{money(offen)}</dd></div>
          {skontoFrist && <div><dt>Skonto bis</dt><dd>{dateLabel(skontoFrist)} ({money(zulaessig)})</dd></div>}
        </dl>
        <div className="finance-modal__grid">
          <label>Betrag
            <input type="number" step="0.01" min="0.01" value={amount} autoFocus
              onChange={(event) => setAmount(Number(event.target.value) || 0)} />
          </label>
          <label>Wertstellung
            <input type="date" value={valueDate} onChange={(event) => setValueDate(event.target.value)} />
          </label>
          <label>Zahlungsart
            <input value={method} onChange={(event) => setMethod(event.target.value)} placeholder="Überweisung, bar, EC …" />
          </label>
          <label>Vermerk
            <input value={note} onChange={(event) => setNote(event.target.value)} />
          </label>
        </div>
        {restNachZahlung > 0 && (
          <p className="finance-source">
            Nach dieser Zahlung bleiben <strong>{money(restNachZahlung)}</strong> offen.
            {skontoMoeglich
              ? ' Das liegt im Rahmen des vereinbarten Skontos — Sie können den Rest unten als Skonto ausbuchen.'
              : zulaessig > 0
                ? ' Das übersteigt den zulässigen Skontoabzug — die Rechnung bleibt teilweise offen.'
                : ' Die Skontofrist ist abgelaufen oder es ist kein Skonto vereinbart.'}
          </p>
        )}
        <div className="finance-modal__actions">
          <button type="button" className="finance-actions__btn" onClick={onClose}>Abbrechen</button>
          {skontoMoeglich && (
            <button type="button" className="finance-actions__btn" disabled={busy}
              onClick={() => onSave({ amount: restNachZahlung, kind: 'cash_discount', valueDate, note: 'Skontoabzug' })}>
              Rest als Skonto ({money(restNachZahlung)})
            </button>
          )}
          <button type="submit" className="finance-actions__btn is-pay" disabled={busy || !(amount > 0)}>
            Zahlung buchen
          </button>
        </div>
      </form>
    </div>
  );
}
